<script lang="ts">
  import { page } from '$app/stores';
  import MobileMenu from './MobileMenu.svelte';
  import { trackCtaClick } from '$lib/utils/analytics';
  
  let scrolled = false;
  let navElement: HTMLElement;
  let mobileMenuOpen = false;
  let productsDropdownOpen = false;
  
  function handleScroll() {
    scrolled = window.scrollY > 40;
  }
  
  function toggleMobileMenu() {
    mobileMenuOpen = !mobileMenuOpen;
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
  }
  
  function closeMobileMenu() {
    mobileMenuOpen = false;
    document.body.style.overflow = '';
  }
</script>

<svelte:window on:scroll={handleScroll} />

<nav bind:this={navElement} class="nav" class:scrolled>
  <div class="nav-container">
    <a href="/" class="nav-logo">
      <img src="/assets/honey-logo.webp" alt="" class="nav-logo-icon" />
      <span class="nav-logo-text">TestMachine</span>
    </a>
    
    <div class="nav-links">
      <a href="/" class="nav-link" class:nav-link--active={$page.url.pathname === '/'}>Home</a>
      
      <div
        class="nav-dropdown"
        role="button"
        tabindex="0"
        aria-haspopup="true"
        aria-expanded={productsDropdownOpen}
        on:mouseenter={() => productsDropdownOpen = true}
        on:mouseleave={() => productsDropdownOpen = false}
      >
        <span class="nav-link" class:nav-link--active={$page.url.pathname.startsWith('/products')}>
          Products
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none" class="dropdown-arrow">
            <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <div class="nav-dropdown-menu" class:show={productsDropdownOpen}>
          <div class="nav-dropdown-grid">
            <a href="/products/azimuth" class="nav-dropdown-card" class:nav-dropdown-card--active={$page.url.pathname === '/products/azimuth'}>
              <span class="nav-dropdown-card-title">Azimuth</span>
              <span class="nav-dropdown-card-desc">AI-powered offensive security for smart contracts.</span>
            </a>
            <a href="/products/token-custody" class="nav-dropdown-card" class:nav-dropdown-card--active={$page.url.pathname === '/products/token-custody'}>
              <span class="nav-dropdown-card-title">Token Custody</span>
              <span class="nav-dropdown-card-desc">Continuous token risk analysis across 11M+ tokens.</span>
            </a>
            <a href="/products/agis" class="nav-dropdown-card" class:nav-dropdown-card--active={$page.url.pathname === '/products/agis'}>
              <span class="nav-dropdown-card-title">AGIS</span>
              <span class="nav-dropdown-card-desc">Proof-based security for autonomous agents with real authority.</span>
            </a>
          </div>
        </div>
      </div>
      
      <a href="/evmbench" class="nav-link" class:nav-link--active={$page.url.pathname === '/evmbench'}>Leaderboard</a>
      <a href="/blog" class="nav-link" class:nav-link--active={$page.url.pathname === '/blog'}>Blog</a>
    </div>
    
    <div class="nav-actions">
      <a href="/#contact" class="btn btn--secondary btn--nav" on:click={() => trackCtaClick('nav_contact', 'Contact Us', '/#contact')}>Contact Us</a>
      <a href="https://app.testmachine.ai/" class="btn btn--primary btn--nav" target="_blank" rel="noopener noreferrer" on:click={() => trackCtaClick('nav_launch_app', 'Community Edition', 'https://app.testmachine.ai/')}>Community Edition</a>
      
      <button 
        class="hamburger" 
        class:active={mobileMenuOpen}
        on:click={toggleMobileMenu}
        aria-label="Toggle menu"
        aria-expanded={mobileMenuOpen}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>
    </div>
  </div>
</nav>

<MobileMenu 
  open={mobileMenuOpen} 
  on:close={closeMobileMenu}
/>

<style>
  .nav {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: var(--nav-h);
    z-index: 1000;
    background: var(--nav-bg);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-bottom: 1px solid var(--editorial-muted);
    transition: border-color var(--transition), background var(--transition);
  }
  
  .nav.scrolled {
    border-bottom-color: var(--editorial-muted);
    background: var(--nav-bg);
  }
  
  .nav-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    max-width: var(--max-w);
    margin: 0 auto;
    padding: 0 clamp(20px, 4vw, 40px);
  }
  
  .nav-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
  
  .nav-logo-icon {
    width: 36px;
    height: 36px;
    object-fit: contain;
    filter: none;
    transition: filter var(--transition);
  }
  
  .nav-logo-text {
    font-family: var(--font-display);
    font-size: 1.35rem;
    font-weight: 700;
    letter-spacing: -0.02em;
  }
  
  .nav-links {
    display: flex;
    align-items: center;
    gap: 32px;
  }
  
  .nav-link {
    font-family: var(--font-mono);
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--fg);
    transition: color var(--transition);
    position: relative;
  }
  
  .nav-link::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 0;
    height: 1px;
    background: var(--editorial);
    transition: width var(--transition);
  }
  
  .nav-link:hover,
  .nav-link.nav-link--active {
    color: var(--fg);
  }
  
  .nav-link:hover::after,
  .nav-link.nav-link--active::after {
    width: 100%;
  }
  
  .nav-link.nav-link--active {
    color: var(--fg);
  }
  
  .nav-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  
  .hamburger {
    display: none;
    width: 40px;
    height: 40px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
    z-index: 1001;
    background: none;
    border: none;
    cursor: pointer;
  }
  
  .hamburger span {
    display: block;
    width: 20px;
    height: 1.5px;
    background: var(--fg);
    transition: all var(--transition);
    transform-origin: center;
  }
  
  .hamburger.active span:nth-child(1) {
    transform: translateY(6.5px) rotate(45deg);
  }
  
  .hamburger.active span:nth-child(2) {
    opacity: 0;
  }
  
  .hamburger.active span:nth-child(3) {
    transform: translateY(-6.5px) rotate(-45deg);
  }
  
  @media (max-width: 1024px) {
    .nav-links {
      display: none;
    }
    
    .hamburger {
      display: flex;
    }
  }
  
  .nav-dropdown {
    position: relative;
  }

  .nav-dropdown .nav-link {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: default;
  }

  .dropdown-arrow {
    transition: transform var(--transition);
  }

  .nav-dropdown:hover .dropdown-arrow {
    transform: rotate(180deg);
  }

  .nav-dropdown-menu {
    position: absolute;
    top: calc(100% + 12px);
    left: 50%;
    transform: translateX(-50%) translateY(-8px);
    background: var(--bg-deeper);
    border: 1px solid var(--border);
    border-radius: 0;
    padding: 12px;
    width: 560px;
    max-width: calc(100vw - 40px);
    box-shadow: 0 16px 48px color-mix(in srgb, var(--fg) 18%, transparent);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    opacity: 0;
    visibility: hidden;
    transition: opacity var(--transition), visibility var(--transition), transform var(--transition);
    z-index: 1000;
  }

  .nav-dropdown-menu.show {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }

  .nav-dropdown-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  .nav-dropdown-card {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    background: transparent;
    border: 1px solid transparent;
    transition: border-color var(--transition), background var(--transition);
  }

  .nav-dropdown-card:hover,
  .nav-dropdown-card--active {
    background: var(--card-bg);
    border-color: var(--editorial);
  }

  .nav-dropdown-card:hover .nav-dropdown-card-title,
  .nav-dropdown-card--active .nav-dropdown-card-title {
    color: var(--fg);
  }

  .nav-dropdown-card-title {
    font-family: var(--font-display);
    font-size: 0.9375rem;
    font-weight: 700;
    color: var(--fg);
    line-height: 1.25;
    transition: color var(--transition);
  }

  .nav-dropdown-card-desc {
    font-family: var(--font-body);
    font-size: 0.75rem;
    line-height: 1.55;
    color: var(--fg-muted);
  }

  @media (max-width: 768px) {
    .nav-logo-icon {
      width: 34px;
      height: 34px;
    }
    
    .nav-logo-text {
      font-size: 1.3rem;
    }
    
    .hamburger {
      width: 44px;
      height: 44px;
    }
  }
</style>
